import { useState } from 'react';
import useGetDrivers from '../../../API/hooks/drivers/useGetDrivers';
import { Driver as DriverType } from '../../../API/types/Drivers';
import DropdownFilter from '../../../components/common/DropdownFilter/DropdownFilter';
import { FilterConfig } from '../../../components/common/DropdownFilter/types';
import Icon from '../../../components/common/Icon/Icon';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import Tags from '../../../components/common/Tags';
import { Button } from '../../../components/ui/Button/Button';
import { queryFilters } from '../../../utils/queryFilters';
import { generatePreviewFullName } from '../../Drivers/Modals/helpers';
import S from './AssignDriverToTripStyles';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import usePostDriverAvatarRequest from '../../../API/hooks/drivers/usePostDriverAvatarRequest';
import LocationThumbnail from '../../../assets/images/preview-location.png';
import Driver from '../../Vehicles/Modals/AssignDeviceToVehicle/Driver';
import useAssignDriverToTrip from '../../../API/hooks/trips/useAssignDriverToTrip';

type Props = {
  onCancel: () => void;
  tripId: string;
  fleetId: string;
};

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { score_DESC: 'Score: most' },
      { score_ASC: 'Score: least' },
    ],
  },
] as FilterConfig[];

function AssignDriverToTrip({
  onCancel,
  tripId,
  fleetId,
}: Props): JSX.Element {
  const driverThumbnail = useDriverThumbnail();
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const { data, isLoading, isFetching } = useGetDrivers(fleetId, queryFilters(filters));
  const [selectedDriver, setSelectedDriver] = useState<DriverType | null>(null);
  const [assignError, setAssignError] = useState('');

  const { mutate: mutateRequestAvatar } = usePostDriverAvatarRequest(() => {}, () => {});

  const onSubmitSuccess = (): void => {
    if (selectedDriver && !selectedDriver.avatar) {
      mutateRequestAvatar({ fleetId, driverId: selectedDriver._id as string });
    }
    setSelectedDriver(null);
    setAssignError('');
    onCancel();
  };

  const onSubmitFail = (error: string): void => {
    setAssignError(error);
  };

  const { mutate } = useAssignDriverToTrip(onSubmitSuccess, onSubmitFail);

  const handleDriverAssignment = (): void => mutate({ driverId: selectedDriver!._id, fleetId, tripId });

  const handleDriverSelection = (driver: DriverType): void => {
    setSelectedDriver(() => driver);
  };

  return (
    <S.Wrapper>
      <div className="titles">
        <div className="action-bar">
          <div className="bar">
            <div className="assignment-container">
              <div className="vehicle-overview">
                <Tags.ItemOverview
                  avatar={LocationThumbnail}
                  avatarAlt="vehicle"
                  title=""
                  subtitle=""
                />
              </div>
              <Icon iconKey={IconKeys.ARROW_RIGHT} />
              <div className="driver-overview">
                {selectedDriver
                  ? (
                    <Tags.Driver
                      imageSrc={selectedDriver.avatarUrl || driverThumbnail}
                      name={
                        generatePreviewFullName(selectedDriver.firstName, selectedDriver.lastName)
                      }
                      licenseName={selectedDriver.licenseName as string}
                    />
                  )
                  : <div className="driver-placeholder"> Choose driver from below </div>}
              </div>
            </div>
            <div className="buttons-container">
              <Button
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                className="confirm-btn"
                disabled={!selectedDriver}
                onClick={handleDriverAssignment}
              >
                Assign driver
              </Button>
            </div>
          </div>
          {!!assignError && <div className="error"> {assignError} </div>}
        </div>
        <div className="drivers-toolbar">
          <h3>{`${data?.results.length || 0} ${data?.results.length === 1 ? 'driver' : 'drivers'}`}</h3>
          {
            SEARCH_CONFIG.map((filterConfig: FilterConfig) => (
              <DropdownFilter
                key={filterConfig.type}
                config={filterConfig}
                filters={filters}
                setFilters={setFilters}
              />
            ))
          }
        </div>
      </div>
      <div className="drivers-list">
        {isLoading && <div> Loading... </div>}
        {!isLoading && data?.results && (
          <ul className="list-container">
              {!!data.results.length
                && data.results.map((item: DriverType) => (
                  <Driver
                    driver={item}
                    isChecked={selectedDriver?._id === item._id}
                    selectDriver={handleDriverSelection}
                  />
                ))}
              {!isFetching && !data.results.length && <div className="no-data">No drivers found</div>}
          </ul>
        )}
      </div>
    </S.Wrapper>
  );
}

export default AssignDriverToTrip;
